// [목적] festival-comments 도메인 REST API 클라이언트 — 축제 상세 댓글 목록/작성/수정/삭제 4개 엔드포인트 매핑
// [이유] festival-comments Spec §5-5 — 폴리오 댓글(folio-social.client.ts L107~161)과 UI·훅을 공유하되
//         대상 키가 다르다(folioId uuid ↔ TourAPI contentId text). 응답은 festivalCommentSchema 로 zod 파싱해
//         BE 계약 위반(환각/스키마 드리프트)을 경계에서 차단한다.
// [사이드 이팩트] POST/PATCH/DELETE: JwtAuthGuard + CsrfGuard 필수 → http.ts 가 CSRF 헤더 자동 첨부.
//                 GET: AnonOrAuthenticatedGuard — 비인증 허용. DELETE 는 204 → http.ts 가 undefined 반환.
//                 미존재/비축제/형식불일치 contentId 는 BE 가 동일하게 404 poi.NOT_FOUND 로 응답(ApiError 로 throw).
// [수정 시 고려사항] contentId·commentId 는 경로 세그먼트에 들어가므로 항상 encodeURIComponent 로 안전화한다.
//                   packages/types 의 festival-comment.ts 가 스키마 SoT — 필드 추가는 그쪽에서만.
//                   폴리오 client 와 달리 total 필드는 BE 미제공(카운터 없음, Spec D-5)이므로 페이지 스키마에 optional 로만 둔다.

import {
  festivalCommentSchema,
  createFestivalCommentInputSchema,
  patchFestivalCommentInputSchema,
  festivalCommentListQuerySchema,
  type FestivalComment,
  type CreateFestivalCommentInput,
  type PatchFestivalCommentInput,
  type FestivalCommentListQuery,
} from '@gangwoncanvas/types';
import { z } from 'zod';

import { fetchWithEnvelope } from './http.js';

// ─── 내부 헬퍼 ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ─── 페이지네이션 응답 스키마 ──────────────────────────────────────────────────

const festivalCommentPageSchema = z.object({
  items: z.array(festivalCommentSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().optional(),
});

export type FestivalCommentPage = z.infer<typeof festivalCommentPageSchema>;

// ─── Comments ─────────────────────────────────────────────────────────────────

/**
 * [목적] GET /festivals/:id/comments — 축제 댓글 목록 (비인증 허용)
 * [이유] 축제 상세 하단 댓글 섹션 렌더링. cursor 페이지네이션(limit 기본 20/최대 50).
 * [사이드 이팩트] parentId 미지정 시 최상위+대댓글이 평탄 반환(폴리오와 동일). soft-delete tombstone 포함.
 * [수정 시 고려사항] q 는 partial 로 검증 — limit default(20) 가 클라이언트에서 주입되지 않도록 한다(서버 기본값 사용).
 */
export async function getComments(
  contentId: string,
  q?: Partial<FestivalCommentListQuery>,
): Promise<FestivalCommentPage> {
  const validated = q ? festivalCommentListQuerySchema.partial().parse(q) : {};
  const query = buildQuery(validated as Record<string, string | number | undefined>);
  const data = await fetchWithEnvelope<unknown>(
    `/festivals/${encodeURIComponent(contentId)}/comments${query}`,
  );
  return festivalCommentPageSchema.parse(data);
}

/**
 * [목적] POST /festivals/:id/comments — 축제 댓글 작성 (인증 필수)
 * [이유] 축제 상세 댓글 작성 폼. 입력은 createFestivalCommentInputSchema 로 사전 검증(text 1~1000자).
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. 금칙어는 BE 가 400 content.BANNED_WORD_DETECTED 로 거절.
 * [수정 시 고려사항] parentId 지정 시 대댓글(1-level) — 현재 FE 답글 입력 UI 는 없음(Spec OQ-1).
 */
export async function addComment(
  contentId: string,
  body: CreateFestivalCommentInput,
): Promise<FestivalComment> {
  const validated = createFestivalCommentInputSchema.parse(body);
  const data = await fetchWithEnvelope<unknown>(
    `/festivals/${encodeURIComponent(contentId)}/comments`,
    { method: 'POST', body: JSON.stringify(validated) },
  );
  return festivalCommentSchema.parse(data);
}

/**
 * [목적] PATCH /festivals/:id/comments/:commentId — 축제 댓글 수정 (인증 필수, 작성자 본인만)
 * [이유] 본인 댓글 오타 수정.
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. 타인 403 / 삭제된 댓글 410 은 ApiError 로 throw.
 * [수정 시 고려사항] patchFestivalCommentInputSchema 로 text 검증.
 */
export async function patchComment(
  contentId: string,
  commentId: string,
  body: PatchFestivalCommentInput,
): Promise<FestivalComment> {
  const validated = patchFestivalCommentInputSchema.parse(body);
  const data = await fetchWithEnvelope<unknown>(
    `/festivals/${encodeURIComponent(contentId)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'PATCH', body: JSON.stringify(validated) },
  );
  return festivalCommentSchema.parse(data);
}

/**
 * [목적] DELETE /festivals/:id/comments/:commentId — 축제 댓글 소프트 삭제 (204)
 * [이유] 본인(또는 admin) 댓글 삭제. row 는 남고 tombstone(body=null, isDeleted=true)으로 표시된다.
 * [사이드 이팩트] 204 → http.ts undefined 반환. 자식 대댓글은 삭제되지 않는다. 폴리오와 달리 owner 삭제 권한은 없음(Spec D-9).
 * [수정 시 고려사항] 이미 삭제된 댓글 재삭제는 BE 가 idempotent 204 로 처리.
 */
export async function deleteComment(contentId: string, commentId: string): Promise<void> {
  await fetchWithEnvelope<void>(
    `/festivals/${encodeURIComponent(contentId)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'DELETE' },
  );
}
